import React, { useEffect } from 'react'
import { Dispatch } from 'redux'
import { connect } from 'react-redux'
import { Form, FormikProps, Field } from 'formik'
import { EditionType } from '@graphics/gfx-common/dist/constants'
import { DialogActions, Button, CircularProgress } from '@material-ui/core'
import { IFile } from '../../types/file'
import { triggerNotification } from '../../actions/notifications'
import {
  splitFileNameExt,
  doExtensionsMatch,
  formatFileName,
  calculateInitialValuesForUploadModalForm,
} from '../../services/file'
import { copyToClipboard } from '../../services/common'
import LinearProgress from '../StyledLinearProgress'
import { Caption } from '../StyledText'
import { CustomTextField } from '../CustomTextField'
import { StyledFileName } from './StyledFileName'
import { UpdateArchiveModalFormFragment } from './UpdateArchiveModalFormFragment'
import { StyledModalContent, StyledThumbnail, FlexibleWrapper } from './styled'

export interface IUploadModalForm {
  showThumbnail: boolean
  fileTemporaryURL: string | null
  file: IFile
  handleClose: () => void
  formikProps: FormikProps<
    ReturnType<typeof calculateInitialValuesForUploadModalForm>
  >
  dispatch: Dispatch
}

export const UploadModalFormComponent = ({
  showThumbnail,
  fileTemporaryURL,
  file,
  handleClose,
  formikProps,
  dispatch,
}: IUploadModalForm) => {
  const { validateForm } = formikProps
  useEffect(() => {
    validateForm()
  }, [validateForm])
  const extensionsMatch = doExtensionsMatch(file)
  const submitDisabled =
    !!formikProps.errors.fileName ||
    !!formikProps.errors.archiveFilesToUpdate ||
    !!file.disableUploadAndShowCloseButton ||
    (file.existingEdition && !extensionsMatch) ||
    !!(file.fileErrors && file.fileErrors.length !== 0)
  const enteredFileName = `${file.name}.${file.extension}`
  const errorMessages =
    (file.uploadError && [file.uploadError]) || file.fileErrors
  return (
    <Form>
      <StyledModalContent>
        {showThumbnail && (
          <StyledThumbnail src={fileTemporaryURL}>
            {!!fileTemporaryURL ? null : <CircularProgress />}
          </StyledThumbnail>
        )}
        <FlexibleWrapper>
          {file.existingEdition && file.fileInstance ? (
            <>
              <StyledFileName size={file.size} title={enteredFileName}>
                {formatFileName(enteredFileName, 40)}
              </StyledFileName>
              {file.existingEdition.editionType ===
              EditionType.GraphicFolder ? (
                <UpdateArchiveModalFormFragment
                  file={file}
                  extensionsMatch={extensionsMatch}
                  formikProps={formikProps}
                />
              ) : (
                <Caption
                  id="update-single-file-message"
                  color="error"
                  title={file.existingEdition.file.fileName}
                >
                  {extensionsMatch
                    ? `Overwrite file ${formatFileName(
                        file.existingEdition.file.fileName,
                        60
                      )} ${
                        file.existingEdition.file.fileName === enteredFileName
                          ? ''
                          : `with ${formatFileName(enteredFileName, 60)}`
                      }?`
                    : `The uploaded file type does not match the type of the stored file (.${
                        splitFileNameExt(file.existingEdition.file.fileName)
                          .extension
                      })`}
                </Caption>
              )}
            </>
          ) : (
            <Field
              name="fileName"
              disabled={!!file.progress}
              label="File name and extension"
              fullWidth
              helperText={
                formikProps.errors.fileName ||
                'A published file name cannot contain reserved symbols or international characters'
              }
              component={CustomTextField}
              endAdornment={`.${file.extension} | ${file.size}`}
              validateImmediately
            />
          )}
          {errorMessages?.map((text, index) => (
            <Caption key={index} className="upload-error" color="error">
              {text}
            </Caption>
          ))}
        </FlexibleWrapper>
      </StyledModalContent>
      {file.progress ? (
        <LinearProgress value={file.progress} />
      ) : (
        <DialogActions>
          {errorMessages && file.correlationId && (
            <Button
              id="copy-button"
              type="button"
              className="copy-button"
              onClick={() => {
                copyToClipboard(
                  `${errorMessages.join('\n')}\nCorrelation ID: ${
                    file.correlationId
                  }`,
                  () => {
                    dispatch(triggerNotification('Copied'))
                  }
                )
              }}
              size="small"
            >
              Copy error
            </Button>
          )}
          <Button
            id="cancel-button"
            type="button"
            className="cancel-button"
            onClick={handleClose}
            size="small"
            autoFocus
          >
            {!!file.disableUploadAndShowCloseButton ? 'Close' : 'Cancel'}
          </Button>
          <Button
            id="action-button"
            type="button"
            color="primary"
            className="action-button"
            size="small"
            variant="contained"
            disabled={submitDisabled}
            // This is form in form.
            // To avoid both forms submitting, we handle submit directly.
            onClick={() => {
              formikProps.handleSubmit()
            }}
          >
            Upload
          </Button>
        </DialogActions>
      )}
    </Form>
  )
}

export const UploadModalForm = connect()(UploadModalFormComponent)
